import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { type VariantProps } from 'class-variance-authority'
import { cn } from '../../helpers/classnames'
import { josefButtonVariants } from './button-variants'

export interface JosefIconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof josefButtonVariants> {
  /**
   * Icon component to display in the button
   */
  icon: ReactNode
  /**
   * Accessible label for screen readers
   */
  'aria-label': string
  /**
   * Whether the button is in a loading state
   */
  isLoading?: boolean
  /**
   * Additional CSS classes
   */
  className?: string
}

/**
 * Josef's custom icon button component
 *
 * A square button designed specifically for icons with proper accessibility.
 * Uses the same color palette and variants as JosefButton.
 *
 * @example
 * ```tsx
 * <JosefIconButton
 *   icon={<PlusIcon className="h-4 w-4" />}
 *   aria-label="Add item"
 *   variant="primary"
 * />
 *
 * <JosefIconButton
 *   icon={<TrashIcon className="h-4 w-4" />}
 *   aria-label="Delete"
 *   variant="destructive"
 *   size="icon-sm"
 * />
 * ```
 */
export const JosefIconButton = forwardRef<HTMLButtonElement, JosefIconButtonProps>(
  (
    {
      icon,
      variant,
      size = 'icon',
      isLoading = false,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled || isLoading}
        className={cn(
          josefButtonVariants({ variant, size }),
          className
        )}
        {...props}
      >
        {isLoading ? (
          <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
        ) : (
          icon
        )}
      </button>
    )
  }
)

JosefIconButton.displayName = 'JosefIconButton'

export default JosefIconButton